import React, { useCallback, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Collapse, IconButton } from '@material-ui/core'
import ExpandMore from '@material-ui/icons/ExpandMore'
import ChevronRight from '@material-ui/icons/ChevronRight'
import DebouncedInput from '../../../../molecules/DebouncedInput'
import { useSyncedTransactionTypeHierarchy } from '../../../../../api/coreData'
import { EMPTY_HIERARCHY_QUERY } from './constants'

const treeContainer = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  height: '100%'
}

const searchRow = {
  marginBottom: '8px'
}

const listContainer = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0
}

const groupHeader = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: '2px 0',
  borderBottom: '1px solid var(--color-neutral-100)',
  userSelect: 'none'
}

const groupLabelStyle = {
  fontWeight: 600,
  fontSize: 'var(--font-size-sm)',
  flex: 1
}

const countBadgeStyle = {
  fontSize: '11px',
  color: 'var(--text-color-subtle)',
  marginRight: '12px'
}

const childRowStyle = {
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '36px'
}

const childLabelStyle = {
  fontSize: 'var(--font-size-sm)',
  flex: 1
}

const typeCodeStyle = {
  fontSize: '11px',
  color: 'var(--text-color-subtle)',
  marginLeft: '8px',
  marginRight: '12px',
  fontFamily: 'var(--font-family-monospace, monospace)'
}

const emptyMsgStyle = {
  padding: '16px',
  color: 'var(--text-color-subtle)',
  fontSize: 'var(--font-size-sm)',
  textAlign: 'center'
}

function useHierarchyData () {
  return useSyncedTransactionTypeHierarchy(EMPTY_HIERARCHY_QUERY)
}

export default function TransactionTypeTree ({ selectedTypes, onChange }) {
  const { data: hierarchy, isLoading } = useHierarchyData()
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState(new Set())

  const selectedSet = useMemo(() => new Set(selectedTypes), [selectedTypes])

  const filtered = useMemo(() => {
    if (!hierarchy?.data) return []
    const term = search.toLowerCase().trim()
    if (!term) return hierarchy.data
    return hierarchy.data
      .map(group => {
        const matchingChildren = group.transactionTypes.filter(tt =>
          tt.name?.toLowerCase().includes(term) || tt.type?.toLowerCase().includes(term)
        )
        if (matchingChildren.length) {
          return { ...group, transactionTypes: matchingChildren }
        }
        if (group.financialTypeName?.toLowerCase().includes(term)) {
          return group
        }
        return null
      })
      .filter(Boolean)
  }, [hierarchy, search])

  const searchForceExpanded = useMemo(() => {
    if (!search.trim()) return null
    return new Set(filtered.map(g => g.financialTypeId))
  }, [search, filtered])

  const isExpanded = useCallback((id) => {
    if (searchForceExpanded) return searchForceExpanded.has(id)
    return expanded.has(id)
  }, [expanded, searchForceExpanded])

  const toggleExpand = useCallback((id) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleGroup = useCallback((group) => {
    const allPks = group.transactionTypes.map(tt => tt.pktranstype)
    const allSelected = allPks.every(pk => selectedSet.has(pk))
    if (allSelected) {
      onChange(selectedTypes.filter(pk => !allPks.includes(pk)))
    } else {
      const merged = new Set([...selectedTypes, ...allPks])
      onChange(Array.from(merged))
    }
  }, [selectedTypes, selectedSet, onChange])

  const toggleItem = useCallback((pktranstype) => {
    if (selectedSet.has(pktranstype)) {
      onChange(selectedTypes.filter(pk => pk !== pktranstype))
    } else {
      onChange([...selectedTypes, pktranstype])
    }
  }, [selectedTypes, selectedSet, onChange])

  const handleSearch = useCallback((val) => {
    setSearch(val)
  }, [])

  if (isLoading) {
    return <div style={emptyMsgStyle}>Loading transaction types...</div>
  }

  if (!hierarchy?.data?.length) {
    return <div style={emptyMsgStyle}>No transaction types available</div>
  }

  return (
    <div style={treeContainer}>
      <div style={searchRow}>
        <DebouncedInput
          placeholder='Search transaction types...'
          value={search}
          onChange={handleSearch}
          delay={200}
          style={{ minWidth: '100%', maxWidth: '100%' }}
        />
      </div>
      <div style={listContainer}>
        {filtered.length === 0 ? (
          <div style={emptyMsgStyle}>No matches</div>
        ) : null}
        {filtered.map(group => {
          const allPks = group.transactionTypes.map(tt => tt.pktranstype)
          const selectedCount = allPks.filter(pk => selectedSet.has(pk)).length
          const allChecked = selectedCount === allPks.length
          const indeterminate = selectedCount > 0 && !allChecked
          const open = isExpanded(group.financialTypeId)

          return (
            <div key={group.financialTypeId}>
              <div style={groupHeader}>
                <IconButton size='small' onClick={() => toggleExpand(group.financialTypeId)}>
                  {open ? <ExpandMore fontSize='small' /> : <ChevronRight fontSize='small' />}
                </IconButton>
                <Checkbox
                  size='small'
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onChange={() => toggleGroup(group)}
                  style={{ padding: '2px' }}
                />
                <span style={groupLabelStyle} onClick={() => toggleExpand(group.financialTypeId)}>
                  {group.financialTypeName}
                </span>
                {selectedCount > 0 ? (
                  <span style={countBadgeStyle}>{selectedCount} of {allPks.length}</span>
                ) : null}
              </div>
              <Collapse in={open}>
                {group.transactionTypes.map(tt => (
                  <div key={tt.pktranstype} style={childRowStyle}>
                    <Checkbox
                      size='small'
                      checked={selectedSet.has(tt.pktranstype)}
                      onChange={() => toggleItem(tt.pktranstype)}
                      style={{ padding: '2px' }}
                    />
                    <span style={childLabelStyle}>{tt.name}</span>
                    <span style={typeCodeStyle}>{tt.type}</span>
                  </div>
                ))}
              </Collapse>
            </div>
          )
        })}
      </div>
    </div>
  )
}

TransactionTypeTree.propTypes = {
  selectedTypes: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired
}
